import { collection, getDocs, query, where } from 'firebase/firestore';

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value.seconds != null) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeName = (value) => String(value || '').trim().toLowerCase();
const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

/**
 * Include duplicate customer records created by 3rd-party imports
 * (same display name or same email, different doc id).
 */
export function relatedCustomerIds(customer, allCustomers = []) {
  const ids = new Set([customer.id]);
  const clientNorm = normalizeName(customer.client);
  const emailNorm = normalizeEmail(customer.email);

  allCustomers.forEach((other) => {
    if (!other?.id || other.id === customer.id) return;
    const otherClient = normalizeName(other.client);
    const otherEmail = normalizeEmail(other.email);
    if (clientNorm && otherClient === clientNorm) ids.add(other.id);
    if (emailNorm && otherEmail && otherEmail === emailNorm) ids.add(other.id);
  });

  return [...ids];
}

function customerNameVariants(customer, allCustomers, relatedIds) {
  const names = new Set();
  const add = (value) => {
    const trimmed = String(value || '').trim();
    if (trimmed) names.add(trimmed);
  };

  add(customer.client);
  add(customer.customerName);

  allCustomers
    .filter((other) => relatedIds.includes(other.id))
    .forEach((other) => add(other.client));

  return [...names];
}

async function mergeOrderSnaps(orderMap, snaps) {
  snaps.forEach((snap) => {
    snap.docs.forEach((orderDoc) => {
      if (!orderMap.has(orderDoc.id)) {
        orderMap.set(orderDoc.id, { id: orderDoc.id, ...orderDoc.data() });
      }
    });
  });
}

/**
 * Load orders for a customer even when a 3rd-party import used a duplicate
 * customer record or wrote customerName without a matching customerId.
 */
export async function fetchOrdersForCustomer(db, customer, { allCustomers = [], includeLines = false } = {}) {
  if (!customer?.id) return [];

  const relatedIds = relatedCustomerIds(customer, allCustomers);
  const nameVariants = customerNameVariants(customer, allCustomers, relatedIds);
  const orderMap = new Map();
  const ordersRef = collection(db, 'orders');

  const idBatches = [];
  for (let i = 0; i < relatedIds.length; i += 10) {
    idBatches.push(relatedIds.slice(i, i + 10));
  }

  const idSnaps = await Promise.all(
    idBatches.map((batch) => getDocs(query(ordersRef, where('customerId', 'in', batch))))
  );
  await mergeOrderSnaps(orderMap, idSnaps);

  if (nameVariants.length > 0) {
    const nameSnaps = await Promise.all(
      nameVariants.map((name) => getDocs(query(ordersRef, where('customerName', '==', name))))
    );
    await mergeOrderSnaps(orderMap, nameSnaps);
  }

  const orders = [...orderMap.values()].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

  if (!includeLines || orders.length === 0) return orders;

  const linesRef = collection(db, 'orderLines');
  const lineBatches = [];
  const orderIds = orders.map((order) => order.id);
  for (let i = 0; i < orderIds.length; i += 10) {
    lineBatches.push(orderIds.slice(i, i + 10));
  }

  const linesByOrder = new Map();
  const lineSnaps = await Promise.all(
    lineBatches.map((batch) => getDocs(query(linesRef, where('orderId', 'in', batch))))
  );

  lineSnaps.forEach((snap) => {
    snap.docs.forEach((lineDoc) => {
      const line = { id: lineDoc.id, ...lineDoc.data() };
      const list = linesByOrder.get(line.orderId) || [];
      list.push(line);
      linesByOrder.set(line.orderId, list);
    });
  });

  return orders.map((order) => ({
    ...order,
    lines: linesByOrder.get(order.id) || order.lines || []
  }));
}
